import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useInView } from 'framer-motion';
import {
  ArrowRight, FileText, Settings, CheckCircle, AlertTriangle,
  Download, Database, Shield, Zap, FileCheck,
  Users, ClipboardList, BookOpen, Award, GitMerge, Search
} from 'lucide-react';
import SiteNavbar from '../components/SiteNavbar';
import AIAssistant from '../components/AIAssistant';
import ScreenshotSlider from '../components/ScreenshotSlider';

const TS_SLIDES = [
  '/mockup/ts/dashboard_redesign.png',
  '/mockup/ts/compliance_analysis_redesign.png',
  '/mockup/ts/product_detail_redesign.png',
  '/mockup/ts/products_directory_redesign.png',
  '/mockup/ts/project_detail_redesign.png',
  '/mockup/ts/projects_list_redesign.png',
  '/mockup/ts/refined_dashboard_with_detailed_analytics.png',
  '/mockup/ts/refined_projects_directory.png',
  '/mockup/ts/specification_metrics_document_view.png',
  '/mockup/ts/specification_metrics_product_sections.png',
  '/mockup/ts/specification_metrics_section_list.png',
  '/mockup/ts/submittal_export_redesign.png',
  '/mockup/ts/submittal_generation_redesign.png',
];

function Section({ children, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

function Badge({ children, variant = 'primary' }) {
  const base = 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase';
  const variants = {
    primary: 'bg-primary/10 text-primary border border-primary/20',
    accent: 'bg-accent/15 text-amber-700 border border-accent/30',
    dark: 'bg-white/10 text-white/80 border border-white/20',
    success: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  };
  return <span className={`${base} ${variants[variant]}`}>{children}</span>;
}

export default function SubmittalsSolution() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const lang = i18n.language;

  const problems = [
    {
      icon: FileText,
      titleEn: 'Scattered Submittal Documents',
      titleAr: 'وثائق مستندات متناثرة',
      descEn: 'Material submittals, datasheets, and spec sheets arrive from multiple suppliers in different formats with no central tracking.',
      descAr: 'مستندات المواد والبيانات التقنية تصل من موردين متعددين بصيغ مختلفة دون تتبع مركزي.',
      color: 'text-red-500', bg: 'bg-red-50',
    },
    {
      icon: Search,
      titleEn: 'Manual Compliance Checking',
      titleAr: 'فحص امتثال يدوي',
      descEn: 'Engineers compare spec clauses to product metrics line by line — hours of tedious cross-referencing per submittal package.',
      descAr: 'يقارن المهندسون بنود المواصفات بمقاييس المنتج سطراً بسطر — ساعات من المقارنة المضنية لكل حزمة.',
      color: 'text-amber-500', bg: 'bg-amber-50',
    },
    {
      icon: GitMerge,
      titleEn: 'Broken Review Workflow',
      titleAr: 'سير مراجعة معطوب',
      descEn: 'No structured approval chain — submittals bounce between engineers, reviewers, and admins via email with no visibility.',
      descAr: 'لا سلسلة موافقة منظمة — المستندات تتنقل بين المهندسين والمراجعين والمشرفين عبر البريد الإلكتروني دون رؤية.',
      color: 'text-blue-500', bg: 'bg-sky-50',
    },
    {
      icon: Shield,
      titleEn: 'No Document Audit Trail',
      titleAr: 'لا سجل مراجعة للوثائق',
      descEn: 'When disputes arise, there is no record of who approved what and when — compliance audits become impossible.',
      descAr: 'عند نشوء نزاعات، لا يوجد سجل لمن وافق وماذا ومتى — عمليات تدقيق الامتثال تصبح مستحيلة.',
      color: 'text-muted', bg: 'bg-white',
    },
  ];

  const ingestionFeatures = [
    { icon: BookOpen, titleEn: 'Spec Sheet Parsing', titleAr: 'تحليل جداول المواصفات', descEn: 'Extract technical clauses and performance requirements automatically from project specs', descAr: 'استخراج البنود التقنية ومتطلبات الأداء تلقائياً من مواصفات المشروع', color: 'text-primary', bg: 'bg-primary/8' },
    { icon: FileCheck, titleEn: 'Datasheet Ingestion', titleAr: 'استيعاب الدتاشيت', descEn: 'Process supplier datasheets in PDF, Excel, or Word — extract all product attributes', descAr: 'معالجة دتاشيت الموردين بـ PDF أو Excel أو Word — استخراج جميع خصائص المنتج', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { icon: Database, titleEn: 'Multi-supplier Support', titleAr: 'دعم موردين متعددين', descEn: 'Handle multiple suppliers per project, each with different datasheet formats and naming', descAr: 'التعامل مع موردين متعددين لكل مشروع بصيغ دتاشيت وتسميات مختلفة', color: 'text-blue-500', bg: 'bg-sky-50' },
    { icon: Zap, titleEn: 'Auto-structured Output', titleAr: 'مخرج منظم تلقائياً', descEn: 'All extracted data normalized into a structured comparison matrix for compliance review', descAr: 'جميع البيانات المستخرجة منسقة في مصفوفة مقارنة منظمة للمراجعة', color: 'text-accent', bg: 'bg-accent/8' },
  ];

  const complianceFeatures = [
    lang === 'ar' ? 'مقارنة بند-بند بين المواصفة والمنتج' : 'Clause-by-clause comparison between spec and product',
    lang === 'ar' ? 'تمييز الامتثال/عدم الامتثال/الجزئي تلقائياً' : 'Automated Compliant / Non-compliant / Partial labeling',
    lang === 'ar' ? 'تقييم ثقة لكل نتيجة مقارنة' : 'Confidence score for every comparison result',
    lang === 'ar' ? 'إنشاء ملخص استثناءات مع توصيات' : 'Exception summary with remediation recommendations',
  ];

  const reviewSteps = [
    {
      role: lang === 'ar' ? 'المهندس' : 'Engineer',
      action: lang === 'ar' ? 'يرفع الدتاشيت ويراجع المقارنة الأولية' : 'Uploads datasheet and reviews initial comparison',
      icon: FileText,
      color: 'border-primary/40 bg-primary/8 text-primary',
    },
    {
      role: lang === 'ar' ? 'المراجع' : 'Reviewer',
      action: lang === 'ar' ? 'يفحص نتائج الامتثال ويوقع على بنود الاستثناء' : 'Checks compliance results and signs off on exception items',
      icon: Search,
      color: 'border-accent/40 bg-accent/8 text-accent',
    },
    {
      role: lang === 'ar' ? 'المشرف' : 'Admin',
      action: lang === 'ar' ? 'يوافق على الحزمة النهائية ويصدر وثيقة المستند الرسمية' : 'Approves the final package and issues the official submittal document',
      icon: Shield,
      color: 'border-emerald-500/40 bg-emerald-50 text-emerald-600',
    },
  ];

  const docTypes = [
    {
      icon: ClipboardList,
      titleEn: 'Material Submittal',
      titleAr: 'مستند المواد',
      descEn: 'Formally structured material submittal document with compliance table, product specs, and approval signature blocks — generated from template.',
      descAr: 'وثيقة مستند مواد منظمة رسمياً مع جدول الامتثال ومواصفات المنتج وكتل التوقيع — مولّدة من قالب.',
      color: 'text-primary', bg: 'bg-primary/8',
    },
    {
      icon: Award,
      titleEn: 'Compliance Statement',
      titleAr: 'بيان الامتثال',
      descEn: 'Signed compliance statement mapping each spec clause to the corresponding product attribute — ready for engineer of record.',
      descAr: 'بيان امتثال موقّع يربط كل بند مواصفة بخاصية المنتج المقابلة — جاهز لمهندس السجل.',
      color: 'text-emerald-600', bg: 'bg-emerald-50',
    },
    {
      icon: FileCheck,
      titleEn: 'Warranty Letter',
      titleAr: 'خطاب الضمان',
      descEn: 'Template-driven warranty letter with product details, warranty period, and supplier details — auto-populated from the verified datasheet.',
      descAr: 'خطاب ضمان مبني على قالب مع تفاصيل المنتج وفترة الضمان وبيانات المورد — مملوء تلقائياً من الدتاشيت المحقق.',
      color: 'text-amber-600', bg: 'bg-amber-50',
    },
  ];

  const outputs = [
    { icon: ClipboardList, titleEn: 'Material Submittal Package', titleAr: 'حزمة مستند المواد', descEn: 'Complete formatted submittal ready for submission', descAr: 'مستند منسق كامل جاهز للتقديم', color: 'text-primary', bg: 'bg-primary/8' },
    { icon: Award, titleEn: 'Compliance Certificate', titleAr: 'شهادة الامتثال', descEn: 'Spec clause vs product metric mapping document', descAr: 'وثيقة تعيين بنود المواصفة مقابل مقاييس المنتج', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { icon: Shield, titleEn: 'Audit Trail Log', titleAr: 'سجل المراجعة', descEn: 'Every review action timestamped and attributed', descAr: 'كل إجراء مراجعة بطابع زمني ومنسوب', color: 'text-blue-500', bg: 'bg-sky-50' },
    { icon: AlertTriangle, titleEn: 'Exception Report', titleAr: 'تقرير الاستثناءات', descEn: 'Non-compliant items with remediation notes', descAr: 'البنود غير المستوفية مع ملاحظات المعالجة', color: 'text-amber-500', bg: 'bg-amber-50' },
  ];

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <SiteNavbar />
      <AIAssistant source="submittals" />

      {/* ── HERO ── */}
      <section className="relative min-h-screen overflow-hidden bg-midnight">
        <div className="absolute inset-0 blueprint-grid-dark opacity-50" />
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full bg-emerald-600/10 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[300px] rounded-full bg-primary/8 blur-[100px] pointer-events-none" />

        <div className={`relative z-10 flex min-h-screen ${isRTL ? 'flex-row-reverse' : ''}`}>

          {/* ── Left: text content ── */}
          <div className="flex-1 flex items-center px-6 sm:px-10 lg:px-16 pt-24 pb-16">
            <div className="max-w-xl w-full">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <Badge variant="dark"><Settings size={12} />{lang === 'ar' ? 'الوحدة ٢' : 'Module 2'}</Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="mt-6 text-4xl sm:text-5xl lg:text-[3rem] font-black text-white leading-[1.06] tracking-tight"
              >
                {lang === 'ar' ? (
                  <>حوّل المستندات إلى <span className="gradient-text-accent">سير عمل رقمي</span> محكوم</>
                ) : (
                  <>Turn Submittals into a <span className="gradient-text-accent">Controlled Digital</span> Workflow</>
                )}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="mt-6 text-base sm:text-lg text-white/60 leading-relaxed"
              >
                {lang === 'ar'
                  ? 'أتمتة كاملة لاستيعاب المواصفات والدتاشيت، وتحليل الامتثال، وسير المراجعة، وتوليد الوثائق — في منصة واحدة محكومة.'
                  : 'Full automation of spec and datasheet ingestion, compliance analysis, review workflow, and document generation — in one governed platform.'}
              </motion.p>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.35 }}
                className="mt-8 flex flex-wrap gap-8"
              >
                {[
                  { val: '90%', label: lang === 'ar' ? 'توفير في وقت فحص الامتثال' : 'Compliance check time saved' },
                  { val: '3x', label: lang === 'ar' ? 'أسرع في اعتماد المستندات' : 'Faster submittal approvals' },
                  { val: '100%', label: lang === 'ar' ? 'قابلية تتبع كل قرار' : 'Decision traceability' },
                ].map(({ val, label }, i) => (
                  <div key={i}>
                    <p className="text-3xl font-black gradient-text-accent">{val}</p>
                    <p className="text-white/45 text-xs mt-0.5">{label}</p>
                  </div>
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.45 }}
                className="mt-8 flex flex-wrap gap-3"
              >
                <button
                  onClick={() => navigate('/contact')}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-all shadow-primary-glow glow-primary-hover"
                >
                  {lang === 'ar' ? 'احجز عرضاً' : 'Book a Demo'}
                  <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
                </button>
                <button
                  onClick={() => navigate('/solutions')}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white/8 text-white font-semibold text-sm hover:bg-white/14 transition-all border border-white/20"
                >
                  {lang === 'ar' ? 'كل الحلول' : 'All Solutions'}
                </button>
              </motion.div>
            </div>
          </div>

          {/* ── Right: full-height slider ── */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? -40 : 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="hidden xl:block w-[52%] relative"
          >
            <ScreenshotSlider
              images={TS_SLIDES}
              interval={3000}
              alt="Submittals Interface"
              cover
              className="absolute inset-0"
            />
            {/* fade edge toward text */}
            <div className={`absolute inset-y-0 ${isRTL ? 'right-0' : 'left-0'} w-20 bg-gradient-to-r ${isRTL ? 'from-transparent to-midnight' : 'from-midnight to-transparent'} z-10 pointer-events-none`} />
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 z-20"
        >
          <span className="text-white/25 text-[10px] tracking-widest uppercase">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.6, repeat: Infinity }}
            className="w-px h-8 bg-gradient-to-b from-white/25 to-transparent"
          />
        </motion.div>
      </section>

      {/* ── PROBLEM SECTION ── */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="primary"><AlertTriangle size={12} />{lang === 'ar' ? 'المشكلة' : 'The Problem'}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">
              {lang === 'ar' ? 'لماذا إدارة المستندات مؤلمة' : 'Why Submittal Management Is Painful'}
            </h2>
            <p className="mt-3 text-body text-sm max-w-xl mx-auto">
              {lang === 'ar'
                ? 'فرق الهندسة تخسر أسابيع في التعامل مع عمليات المستندات المتفككة.'
                : 'Engineering teams lose weeks dealing with fragmented submittal operations.'}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {problems.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-border-light card-hover"
              >
                <div className={`w-10 h-10 rounded-xl ${p.bg} flex items-center justify-center mb-4`}>
                  <p.icon size={18} className={p.color} />
                </div>
                <h3 className="text-heading font-bold text-sm mb-2">{lang === 'ar' ? p.titleAr : p.titleEn}</h3>
                <p className="text-body text-xs leading-relaxed">{lang === 'ar' ? p.descAr : p.descEn}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── SPEC + DATASHEET INGESTION ── */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="primary"><BookOpen size={12} />{lang === 'ar' ? 'استيعاب البيانات' : 'Data Ingestion'}</Badge>
            <h2 className="mt-4 text-3xl font-black text-heading">
              {lang === 'ar' ? 'استيعاب المواصفات والدتاشيت تلقائياً' : 'Automatic Spec & Datasheet Ingestion'}
            </h2>
            <p className="mt-3 text-body text-sm max-w-xl mx-auto">
              {lang === 'ar'
                ? 'ارفع المواصفات والدتاشيت بأي صيغة — يستخرج النظام البيانات التقنية ويبنيها في مصفوفة مقارنة آلياً.'
                : 'Upload specs and datasheets in any format — the system extracts technical data and builds a comparison matrix automatically.'}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {ingestionFeatures.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-border-light card-hover"
              >
                <div className={`w-10 h-10 rounded-xl ${f.bg} flex items-center justify-center mb-4`}>
                  <f.icon size={18} className={f.color} />
                </div>
                <h3 className="text-heading font-bold text-sm mb-2">{lang === 'ar' ? f.titleAr : f.titleEn}</h3>
                <p className="text-body text-xs leading-relaxed">{lang === 'ar' ? f.descAr : f.descEn}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── COMPLIANCE ANALYSIS ── */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="success"><CheckCircle size={12} />{lang === 'ar' ? 'تحليل الامتثال' : 'Compliance Analysis'}</Badge>
            <h2 className="mt-4 text-3xl font-black text-heading">
              {lang === 'ar' ? 'مقارنة آلية: المواصفة مقابل المنتج' : 'Automated Comparison: Spec vs Product'}
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Side by side comparison visual */}
            <div className="space-y-3">
              {[
                { specEn: 'Airflow: min 400 CFM', specAr: 'تدفق هواء: ٤٠٠ CFM حد أدنى', prodEn: 'Airflow: 450 CFM', prodAr: 'تدفق هواء: ٤٥٠ CFM', status: 'compliant' },
                { specEn: 'Throw distance: 5–8m', specAr: 'مسافة الرمي: ٥–٨م', prodEn: 'Throw distance: 7.2m', prodAr: 'مسافة الرمي: ٧.٢م', status: 'compliant' },
                { specEn: 'Noise level: max 35 dB(A)', specAr: 'مستوى الضوضاء: ٣٥ dB(A) حد أقصى', prodEn: 'Noise level: 38 dB(A)', prodAr: 'مستوى الضوضاء: ٣٨ dB(A)', status: 'exception' },
                { specEn: 'Material: Galvanized steel', specAr: 'المادة: صلب مجلفن', prodEn: 'Material: Galvanized steel', prodAr: 'المادة: صلب مجلفن', status: 'compliant' },
              ].map((row, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12 }}
                  className={`grid grid-cols-[1fr_auto_1fr] gap-3 p-3 rounded-xl border ${
                    row.status === 'exception' ? 'border-amber-200 bg-amber-50' : 'border-emerald-100 bg-white'
                  }`}
                >
                  <div className="text-xs">
                    <div className="text-muted text-[10px] uppercase font-semibold mb-0.5">{lang === 'ar' ? 'المواصفة' : 'Spec Clause'}</div>
                    <div className="text-body font-medium">{lang === 'ar' ? row.specAr : row.specEn}</div>
                  </div>
                  <div className="flex items-center justify-center">
                    {row.status === 'compliant' ? (
                      <CheckCircle size={16} className="text-emerald-500" />
                    ) : (
                      <AlertTriangle size={16} className="text-amber-500" />
                    )}
                  </div>
                  <div className="text-xs text-right" dir="ltr">
                    <div className="text-muted text-[10px] uppercase font-semibold mb-0.5">{lang === 'ar' ? 'المنتج' : 'Product'}</div>
                    <div className={`font-medium ${row.status === 'exception' ? 'text-amber-700' : 'text-body'}`}>
                      {lang === 'ar' ? row.prodAr : row.prodEn}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Features list */}
            <div>
              <h3 className="text-heading font-bold text-xl mb-6">
                {lang === 'ar' ? 'ماذا يفعل محرك تحليل الامتثال' : 'What the Compliance Engine Does'}
              </h3>
              <ul className="space-y-3 mb-8">
                {complianceFeatures.map((f, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle size={15} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-body text-sm">{f}</span>
                  </motion.li>
                ))}
              </ul>

              <div className="rounded-2xl overflow-hidden border border-border-light shadow-card-hover">
                <img src="/mockup/ts/compliance_analysis_redesign.png" alt="Compliance Interface" className="w-full" />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ── REVIEW WORKFLOW ── */}
      <Section className="py-24 bg-midnight relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid-dark opacity-40" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-emerald-600/8 blur-[100px]" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="dark"><Users size={12} />{lang === 'ar' ? 'سير المراجعة' : 'Review Workflow'}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-white">
              {lang === 'ar' ? 'سلسلة اعتماد منظمة من ٣ خطوات' : '3-Step Structured Approval Chain'}
            </h2>
            <p className="mt-3 text-white/55 text-sm">
              {lang === 'ar'
                ? 'كل خطوة موثقة، كل موافقة مسجلة، كل استثناء مرئي.'
                : 'Every step documented, every approval recorded, every exception visible.'}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className={`flex flex-col lg:flex-row items-stretch gap-6 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
              {reviewSteps.map((step, i) => (
                <React.Fragment key={i}>
                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.15 }}
                    className={`flex-1 rounded-2xl border p-6 ${step.color.includes('primary') ? 'border-primary/40 bg-primary/8' : step.color.includes('accent') ? 'border-accent/40 bg-deep-surface/50' : 'border-emerald-500/30 bg-deep-surface/50'}`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${step.color.includes('primary') ? 'bg-primary/15' : step.color.includes('accent') ? 'bg-accent/15' : 'bg-emerald-500/15'}`}>
                        <step.icon size={18} className={step.color.includes('primary') ? 'text-primary' : step.color.includes('accent') ? 'text-accent' : 'text-emerald-500'} />
                      </div>
                      <div>
                        <div className="text-white/40 text-[10px] uppercase font-semibold">{lang === 'ar' ? `خطوة ${i + 1}` : `Step ${i + 1}`}</div>
                        <div className="text-white font-bold text-sm">{step.role}</div>
                      </div>
                    </div>
                    <p className="text-muted text-xs leading-relaxed">{step.action}</p>
                  </motion.div>

                  {i < reviewSteps.length - 1 && (
                    <div className="flex items-center justify-center">
                      <motion.div
                        animate={{ x: [0, 4, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="text-primary/50 hidden lg:block"
                      >
                        <ArrowRight size={20} className={isRTL ? 'rotate-180' : ''} />
                      </motion.div>
                      <div className="w-px h-6 bg-dark-border lg:hidden" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="mt-12 max-w-3xl mx-auto">
            <div className="rounded-2xl overflow-hidden border border-dark-border shadow-dark-card">
              <img src="/mockup/ts/refined_dashboard_with_detailed_analytics.png" alt="Review Workflow" className="w-full" />
            </div>
          </div>
        </div>
      </Section>

      {/* ── DOCUMENT GENERATION ── */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="primary"><Download size={12} />{lang === 'ar' ? 'توليد الوثائق' : 'Document Generation'}</Badge>
            <h2 className="mt-4 text-3xl font-black text-heading">
              {lang === 'ar' ? '٣ أنواع وثائق مولّدة آلياً' : '3 Document Types Auto-Generated'}
            </h2>
            <p className="mt-3 text-body text-sm max-w-xl mx-auto">
              {lang === 'ar'
                ? 'وثائق رسمية جاهزة للتقديم، مولّدة من قوالب محكومة وملؤها تلقائياً من البيانات المحققة.'
                : 'Submission-ready formal documents generated from governed templates and auto-populated from verified data.'}
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {docTypes.map((doc, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-border-light card-hover"
              >
                <div className={`w-10 h-10 rounded-xl ${doc.bg} flex items-center justify-center mb-4`}>
                  <doc.icon size={18} className={doc.color} />
                </div>
                <h3 className="text-heading font-bold text-base mb-3">{lang === 'ar' ? doc.titleAr : doc.titleEn}</h3>
                <p className="text-body text-xs leading-relaxed">{lang === 'ar' ? doc.descAr : doc.descEn}</p>
                <div className="mt-4 pt-4 border-t border-border-light">
                  <span className="text-[10px] text-muted uppercase font-semibold tracking-wide">
                    {lang === 'ar' ? 'صيغ التصدير: ' : 'Export formats: '}
                  </span>
                  <span className="text-[10px] text-body">PDF, DOCX</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── OUTPUTS ── */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="primary"><ClipboardList size={12} />{lang === 'ar' ? 'المخرجات' : 'Final Outputs'}</Badge>
            <h2 className="mt-4 text-3xl font-black text-heading">
              {lang === 'ar' ? 'ما تحصل عليه في النهاية' : 'What You Get at the End'}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {outputs.map((o, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-border-light card-hover"
              >
                <div className={`w-10 h-10 rounded-xl ${o.bg} flex items-center justify-center mb-4`}>
                  <o.icon size={18} className={o.color} />
                </div>
                <h3 className="text-heading font-bold text-sm mb-2">{lang === 'ar' ? o.titleAr : o.titleEn}</h3>
                <p className="text-body text-xs leading-relaxed">{lang === 'ar' ? o.descAr : o.descEn}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── FINAL CTA ── */}
      <Section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-50" />
        <div className="absolute top-0 right-1/3 w-72 h-72 rounded-full bg-emerald-500/5 blur-[80px]" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-black text-heading">
            {lang === 'ar' ? 'جاهز لتحويل سير عمل المستندات؟' : 'Ready to Transform Your Submittal Workflow?'}
          </h2>
          <p className="mt-4 text-body leading-relaxed text-sm max-w-xl mx-auto">
            {lang === 'ar'
              ? 'احجز عرضاً مباشراً وشاهد كيف يحوّل تولي عمليات المستندات التقنية من فوضى يدوية إلى سير عمل رقمي محكوم.'
              : 'Book a live demo and see how Tuly transforms technical submittal operations from manual chaos into a controlled digital workflow.'}
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary-hover transition-all shadow-primary-glow glow-primary-hover"
            >
              {lang === 'ar' ? 'احجز عرضاً' : 'Book a Demo'}
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </button>
            <button
              onClick={() => navigate('/solutions')}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl border-2 border-border-light text-body font-semibold hover:border-primary/30 hover:text-primary transition-all"
            >
              {lang === 'ar' ? 'جميع الحلول' : 'All Solutions'}
            </button>
          </div>
        </div>
      </Section>

      {/* ── FOOTER ── */}
      <footer className="bg-midnight border-t border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/tuly.png" alt="Tuly Technology" className="h-8 w-auto" />
                <span className="text-white font-bold text-sm">Tuly Technology</span>
              </div>
              <p className="text-muted text-xs leading-relaxed">{t('footer.tagline')}</p>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.solutions')}</h4>
              <ul className="space-y-2.5">
                {[['boq', '/solutions/boq'], ['submittals', '/solutions/submittals'], ['platform', '/platform']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.company')}</h4>
              <ul className="space-y-2.5">
                {[['about', '/about'], ['contact', '/contact']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.legal')}</h4>
              <ul className="space-y-2.5">
                {[['privacy', '/privacy'], ['terms', '/terms']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-dark-border flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-muted text-xs">© {new Date().getFullYear()} Tuly Technology. {t('footer.allRights')}</p>
            <p className="text-muted/50 text-xs">{t('footer.madeWith')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
